// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  extractPatientIdsFromMembership,
  patientToSwePatientRow,
  resolvePatientAccessPolicyRef,
  sortSwePatientRows,
} from '../utils/swePatientAccess';

export interface SwePatientRow {
  id: string;
  name: string;
  mrn: string;
  dob: string;
}

export interface UseSwePatients {
  patients: SwePatientRow[];
  loading: boolean;
  error: string | undefined;
  refresh: () => void;
  reload: () => void;
}

const PATIENT_PAGE_SIZE = 500;

export function useSwePatients(sweId: string | undefined): UseSwePatients {
  const medplum = useMedplum();
  const [patients, setPatients] = useState<SwePatientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [fetchKey, setFetchKey] = useState(0);
  const noCacheRef = useRef(false);

  const refresh = useCallback(() => {
    noCacheRef.current = false;
    setFetchKey((k) => k + 1);
  }, []);

  const reload = useCallback(() => {
    noCacheRef.current = true;
    setFetchKey((k) => k + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchKey is an intentional trigger counter — it increments to force a re-fetch without being read inside the effect body
  useEffect(() => {
    if (!sweId) {
      setPatients([]);
      setLoading(false);
      return;
    }

    let active = true;
    const noCache = noCacheRef.current;
    noCacheRef.current = false;

    setLoading(true);
    setError(undefined);

    async function load(): Promise<void> {
      const searchOptions = noCache ? { cache: 'no-cache' as const } : undefined;

      const patientPolicyRef = await resolvePatientAccessPolicyRef(medplum);
      const membership = await medplum.searchOne(
        'ProjectMembership',
        { profile: `Practitioner/${sweId}` },
        searchOptions
      );

      const patientIds = extractPatientIdsFromMembership(membership, patientPolicyRef);
      if (patientIds.length === 0) {
        if (!active) {
          return;
        }
        setPatients([]);
        return;
      }

      const patientResults = await medplum.searchResources(
        'Patient',
        {
          _id: patientIds.join(','),
          _count: String(PATIENT_PAGE_SIZE),
          _sort: 'name',
        },
        searchOptions
      );

      const nextRows = sortSwePatientRows(patientResults.map(patientToSwePatientRow));

      console.debug('[useSwePatients] loaded', {
        sweId,
        patientCount: nextRows.length,
      });

      if (!active) {
        return;
      }
      setPatients(nextRows);
    }

    load()
      .catch((err) => {
        console.error('[useSwePatients] Failed to load patients:', err);
        if (active) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, sweId, fetchKey]);

  return { patients, loading, error, refresh, reload };
}
